import { useEffect, useState, type CSSProperties, type DragEvent } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, qs, type KanbanCard, type ProjectPhase } from '../api';
import { DemoBadge, Dialog, Empty, ErrorAlert, Field, fieldError, Loading } from '../components/ui';
import { date, PROJECT_STATUS_LABEL } from '../format';

// Cor da fase aplicada por variável CSS: cabeçalho da coluna, faixa do card e etiquetas.
const corFase = (color?: string | null) => ({ '--fase': color || '#64748B' }) as CSSProperties;

export function PhaseBadge({ name, color }: { name?: string | null; color?: string | null }) {
  if (!name) return <span className="badge">Sem fase</span>;
  return (
    <span className="badge phase-badge" style={corFase(color)}>
      {name}
    </span>
  );
}

// Alternância Lista | Kanban no topo das telas de projetos.
export function ProjectsViewToggle({ current }: { current: 'lista' | 'kanban' }) {
  return (
    <div className="segmented" role="group" aria-label="Visualização">
      <Link to="/projetos" className="segmented-link" aria-current={current === 'lista' ? 'page' : undefined}>
        Lista
      </Link>
      <Link to="/projetos/kanban" className="segmented-link" aria-current={current === 'kanban' ? 'page' : undefined}>
        Kanban
      </Link>
    </div>
  );
}

const diasDesde = (iso: string | null) => (iso ? Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 86400000)) : null);
const iniciais = (nome: string) =>
  nome
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]!.toUpperCase())
    .join('');
const hojeIso = () => new Date().toISOString().slice(0, 10);

type Coluna = { id: string | null; name: string; color: string };

// Quadro no estilo Trello: listas lado a lado, cards com capa na cor da fase, menu ⋯ e "+ Adicionar projeto".
export function KanbanPage() {
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const [scope, setScope] = useState<'all' | 'linked'>('all');
  const [includeClosed, setIncludeClosed] = useState(false);
  const [dragId, setDragId] = useState<string | null>(null);
  const [overPhase, setOverPhase] = useState<string | null>(null);
  // menu ⋯ aberto: posição fixa junto ao botão (não é cortado pela rolagem da lista)
  const [menu, setMenu] = useState<{ id: string; top: number; left: number } | null>(null);
  const menuCard = menu?.id ?? null;
  const setMenuCard = (v: null) => setMenu(v);
  useEffect(() => {
    if (!menu) return;
    const fechar = (e: KeyboardEvent) => e.key === 'Escape' && setMenu(null);
    window.addEventListener('keydown', fechar);
    return () => window.removeEventListener('keydown', fechar);
  }, [menu]);
  const key = ['projects-kanban', search, scope, includeClosed];
  const q = useQuery({
    queryKey: key,
    queryFn: () => api.get<{ phases: ProjectPhase[]; projects: KanbanCard[] }>(`/projects/kanban${qs({ q: search, scope, includeClosed })}`),
    placeholderData: (prev) => prev,
  });
  const mover = useMutation({
    mutationFn: ({ card, phaseId }: { card: KanbanCard; phaseId: string | null }) => api.post(`/projects/${card.id}/phase`, { phaseId, version: card.version }),
    // o card muda de lista na hora; se o servidor recusar, a tela volta ao estado real
    onMutate: async ({ card, phaseId }) => {
      setMenuCard(null);
      await qc.cancelQueries({ queryKey: key });
      const antes = qc.getQueryData<{ phases: ProjectPhase[]; projects: KanbanCard[] }>(key);
      if (antes) qc.setQueryData(key, { ...antes, projects: antes.projects.map((p) => (p.id === card.id ? { ...p, phaseId, phaseChangedAt: new Date().toISOString() } : p)) });
      return { antes };
    },
    onError: (_e, _v, ctx) => ctx?.antes && qc.setQueryData(key, ctx.antes),
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['projects-kanban'] });
      qc.invalidateQueries({ queryKey: ['projects'] });
    },
  });

  const data = q.data;
  const colunas: Coluna[] = data
    ? [...data.phases.map((p) => ({ id: p.id as string | null, name: p.name, color: p.color })), ...(data.projects.some((c) => !c.phaseId) ? [{ id: null, name: 'Sem fase', color: '#94A3B8' }] : [])]
    : [];
  const soltar = (e: DragEvent, phaseId: string | null) => {
    e.preventDefault();
    setOverPhase(null);
    const card = data?.projects.find((c) => c.id === (dragId ?? e.dataTransfer.getData('text/plain')));
    setDragId(null);
    if (card && card.phaseId !== phaseId) mover.mutate({ card, phaseId });
  };

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Projetos — Kanban</h1>
          <p>Arraste o card para outra lista para mudar o projeto de fase, ou use o menu ⋯ do card.</p>
        </div>
        <div className="actions">
          <ProjectsViewToggle current="kanban" />
          <Link className="btn" to="/cadastros/fases">
            Fases e cores
          </Link>
          <Link className="btn btn-primary" to="/projetos/novo">
            Novo projeto
          </Link>
        </div>
      </div>
      <div className="toolbar">
        <label className="visually-hidden" htmlFor="k-search">Pesquisar</label>
        <input id="k-search" type="search" placeholder="Pesquisar por projeto, código ou cliente" value={search} onChange={(e) => setSearch(e.target.value)} />
        <div className="segmented" role="group" aria-label="Escopo">
          <button aria-pressed={scope === 'all'} onClick={() => setScope('all')}>
            Todos
          </button>
          <button aria-pressed={scope === 'linked'} onClick={() => setScope('linked')}>
            Meus
          </button>
        </div>
        <label className="check">
          <input type="checkbox" checked={includeClosed} onChange={(e) => setIncludeClosed(e.target.checked)} /> Mostrar encerrados
        </label>
      </div>

      <ErrorAlert error={q.error} />
      <ErrorAlert error={mover.error} title="Não foi possível mudar a fase." />
      {q.isLoading ? (
        <Loading />
      ) : !data || data.phases.length === 0 ? (
        <Empty>
          Nenhuma fase cadastrada. Cadastre as fases em <Link to="/cadastros/fases">Cadastros › Fases de projeto</Link>.
        </Empty>
      ) : (
        <div className="board" onScrollCapture={() => menu && setMenu(null)} onClick={(e) => !(e.target as HTMLElement).closest('.card-menu, .card-menu-btn') && setMenuCard(null)}>
          <div className="board-lists" role="list" aria-label="Fases do projeto">
            {colunas.map((col) => {
              const cards = data.projects.filter((c) => c.phaseId === col.id);
              const chave = col.id ?? 'sem-fase';
              const arrastandoDeOutra = dragId !== null && data.projects.find((c) => c.id === dragId)?.phaseId !== col.id;
              return (
                <section
                  key={chave}
                  role="listitem"
                  className={`board-list ${overPhase === chave ? 'is-over' : ''}`}
                  style={corFase(col.color)}
                  aria-label={`${col.name}: ${cards.length} projeto(s)`}
                  onDragOver={(e) => {
                    e.preventDefault();
                    e.dataTransfer.dropEffect = 'move';
                    setOverPhase(chave);
                  }}
                  onDragLeave={(e) => {
                    if (!e.currentTarget.contains(e.relatedTarget as Node)) setOverPhase(null);
                  }}
                  onDrop={(e) => soltar(e, col.id)}
                >
                  <header className="board-list-head">
                    <span className="board-list-name">{col.name}</span>
                    <span className="board-list-count">{cards.length}</span>
                  </header>
                  <div className="board-cards">
                    {cards.map((c) => {
                      const dias = diasDesde(c.phaseChangedAt);
                      const atrasado = c.endDate && c.endDate < hojeIso() && c.status === 'ativo';
                      return (
                        <article
                          key={c.id}
                          className={`board-card ${dragId === c.id ? 'is-dragging' : ''}`}
                          draggable
                          onDragStart={(e) => {
                            setDragId(c.id);
                            setMenuCard(null);
                            e.dataTransfer.setData('text/plain', c.id);
                            e.dataTransfer.effectAllowed = 'move';
                          }}
                          onDragEnd={() => {
                            setDragId(null);
                            setOverPhase(null);
                          }}
                        >
                          <div className="board-card-cover" aria-hidden />
                          <div className="board-card-body">
                            <div className="board-card-labels">
                              <span className="board-label">{col.name}</span>
                              {c.status !== 'ativo' && <span className="board-label board-label-off">{PROJECT_STATUS_LABEL[c.status]}</span>}
                              {c.linkedToMe && <span className="board-label board-label-meu">Meu</span>}
                              <DemoBadge show={c.isDemo} />
                            </div>
                            <Link to={`/projetos/${c.id}`} className="board-card-title" draggable={false}>
                              {c.name}
                            </Link>
                            <div className="board-card-client">{c.clientTradeName || c.clientName}</div>
                            <div className="board-card-badges">
                              {(c.startDate || c.endDate) && (
                                <span className={`board-badge ${atrasado ? 'is-late' : ''}`} title={atrasado ? 'Prazo final já passou' : 'Período do projeto'}>
                                  🗓 {date(c.startDate)} – {date(c.endDate)}
                                </span>
                              )}
                              {dias !== null && (
                                <span className="board-badge" title="Tempo nesta fase">
                                  ⏱ {dias === 0 ? 'hoje' : `${dias}d`}
                                </span>
                              )}
                              <span className="board-badge muted">{c.code}</span>
                              <span className="board-avatar" title={`Responsável: ${c.ownerName}`}>
                                {iniciais(c.ownerName)}
                              </span>
                            </div>
                          </div>
                          <button
                            type="button"
                            className="card-menu-btn"
                            aria-label={`Mover ${c.name} para outra fase`}
                            aria-expanded={menuCard === c.id}
                            onClick={(e) => {
                              if (menuCard === c.id) return setMenu(null);
                              const r = e.currentTarget.getBoundingClientRect();
                              setMenu({ id: c.id, top: Math.min(r.bottom + 4, window.innerHeight - 320), left: Math.max(8, Math.min(r.right - 230, window.innerWidth - 240)) });
                            }}
                          >
                            ⋯
                          </button>
                          {menuCard === c.id && (
                            <div className="card-menu" role="menu" aria-label="Mover para" style={{ top: menu!.top, left: menu!.left }}>
                              <div className="card-menu-title">Mover para</div>
                              {data.phases
                                .filter((p) => p.active)
                                .map((p) => (
                                  <button
                                    key={p.id}
                                    type="button"
                                    role="menuitem"
                                    className="card-menu-item"
                                    style={corFase(p.color)}
                                    disabled={p.id === c.phaseId || mover.isPending}
                                    onClick={() => mover.mutate({ card: c, phaseId: p.id })}
                                  >
                                    <span className="phase-dot" aria-hidden /> {p.name}
                                    {p.id === c.phaseId && <span className="small muted"> (atual)</span>}
                                  </button>
                                ))}
                              <Link className="card-menu-item" to={`/projetos/${c.id}`}>
                                Abrir projeto
                              </Link>
                            </div>
                          )}
                        </article>
                      );
                    })}
                    {arrastandoDeOutra && overPhase === chave && <div className="board-drop-ghost" aria-hidden />}
                  </div>
                  {col.id && (
                    <Link className="board-add" to={`/projetos/novo?fase=${col.id}`}>
                      + Adicionar projeto
                    </Link>
                  )}
                </section>
              );
            })}
          </div>
        </div>
      )}
    </>
  );
}

// ------------------------------------------------------------------ cadastro de fases

const CORES_SUGERIDAS = ['#64748B', '#7C3AED', '#0B5C8C', '#2563EB', '#D97706', '#DC2626', '#0D9488', '#16A34A', '#DB2777', '#A16207'];

export function PhasesPage() {
  const qc = useQueryClient();
  const [editing, setEditing] = useState<ProjectPhase | 'new' | null>(null);
  const q = useQuery({ queryKey: ['project-phases'], queryFn: () => api.get<{ data: ProjectPhase[] }>('/project-phases') });
  const atualizar = () => {
    qc.invalidateQueries({ queryKey: ['project-phases'] });
    qc.invalidateQueries({ queryKey: ['projects-kanban'] });
  };
  const reordenar = useMutation({ mutationFn: (ids: string[]) => api.post('/project-phases/reorder', { ids }), onSettled: atualizar });
  const excluir = useMutation({ mutationFn: (id: string) => api.del(`/project-phases/${id}`), onSuccess: atualizar });
  const fases = q.data?.data ?? [];
  const mover = (i: number, d: -1 | 1) => {
    const ids = fases.map((f) => f.id);
    [ids[i], ids[i + d]] = [ids[i + d]!, ids[i]!];
    reordenar.mutate(ids);
  };

  return (
    <>
      <div className="page-head">
        <div>
          <p className="small">
            <Link to="/cadastros">← Cadastros</Link> · <Link to="/projetos/kanban">Kanban</Link>
          </p>
          <h1>Fases de projeto</h1>
          <p>As fases são as colunas do Kanban, nesta ordem. Fase já usada é inativada, não excluída.</p>
        </div>
        <div className="actions">
          <button className="btn btn-primary" onClick={() => setEditing('new')}>
            Nova fase
          </button>
        </div>
      </div>
      <ErrorAlert error={q.error || reordenar.error || excluir.error} />
      {q.isLoading ? (
        <Loading />
      ) : (
        <div className="list">
          {fases.map((f, i) => (
            <div key={f.id} className="list-item phase-row" style={corFase(f.color)}>
              <span className="phase-dot" aria-hidden />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="title">
                  {f.name} {!f.active && <span className="badge badge-off">Inativa</span>}
                </div>
                <div className="meta">{f.projectCount} projeto(s)</div>
              </div>
              <div className="actions">
                <button className="btn btn-sm" disabled={i === 0 || reordenar.isPending} onClick={() => mover(i, -1)} aria-label={`Subir ${f.name}`}>
                  ↑
                </button>
                <button className="btn btn-sm" disabled={i === fases.length - 1 || reordenar.isPending} onClick={() => mover(i, 1)} aria-label={`Descer ${f.name}`}>
                  ↓
                </button>
                <button className="btn btn-sm" onClick={() => setEditing(f)}>
                  Editar
                </button>
                {f.projectCount === 0 && (
                  <button className="btn btn-sm btn-danger" onClick={() => confirm(`Excluir a fase "${f.name}"?`) && excluir.mutate(f.id)}>
                    Excluir
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
      {editing && (
        <PhaseDialog
          phase={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            atualizar();
          }}
        />
      )}
    </>
  );
}

function PhaseDialog({ phase, onClose, onSaved }: { phase: ProjectPhase | null; onClose: () => void; onSaved: () => void }) {
  const [name, setName] = useState(phase?.name ?? '');
  const [color, setColor] = useState(phase?.color ?? CORES_SUGERIDAS[0]!);
  const [active, setActive] = useState(phase?.active ?? true);
  const salvar = useMutation({
    mutationFn: () => (phase ? api.put(`/project-phases/${phase.id}`, { name, color, active, version: phase.version }) : api.post('/project-phases', { name, color, active })),
    onSuccess: onSaved,
  });
  return (
    <Dialog title={phase ? 'Editar fase' : 'Nova fase'} onClose={onClose}>
      <form
        className="form"
        onSubmit={(e) => {
          e.preventDefault();
          salvar.mutate();
        }}
      >
        <ErrorAlert error={salvar.error} />
        <Field label="Nome" required error={fieldError(salvar.error, 'name')}>
          {(p) => <input {...p} value={name} maxLength={80} onChange={(e) => setName(e.target.value)} />}
        </Field>
        <Field label="Cor" hint="Usada no topo da coluna, na faixa do card e na etiqueta da fase.">
          {(p) => (
            <div className="phase-cores">
              <input {...p} type="color" value={color} onChange={(e) => setColor(e.target.value.toUpperCase())} />
              {CORES_SUGERIDAS.map((c) => (
                <button key={c} type="button" className={`phase-cor ${c === color ? 'is-sel' : ''}`} style={corFase(c)} onClick={() => setColor(c)} aria-label={`Cor ${c}`} />
              ))}
            </div>
          )}
        </Field>
        <div style={{ margin: '0.5rem 0 1rem' }}>
          Prévia: <PhaseBadge name={name || 'Fase'} color={color} />
        </div>
        <label className="check">
          <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} /> Ativa (aparece como coluna e opção de mudança)
        </label>
        <div className="actions" style={{ marginTop: '1rem' }}>
          <button type="button" className="btn" onClick={onClose}>
            Cancelar
          </button>
          <button className="btn btn-primary" disabled={salvar.isPending}>
            Salvar
          </button>
        </div>
      </form>
    </Dialog>
  );
}
